import React from "react";
import { isScaleLikeType, scaleBounds } from "./formFieldHelpers";

import FormInfoScreen from "./FormInfoScreen";
import FormFieldImage from "./FormFieldImage";

// A non-interactive rendering of a single form field. With `answered`, it shows
// the submitted `value` (filled box / checked / highlighted option or scale);
// without it, it shows the empty "what the employee sees" preview. Used by the
// preview surfaces (FormPreviewModal, section editor, track/module previews) and
// the submission review modal (Records).
//
// `showScoring` reveals per-option points on single_select fields — i.e. which
// answer is correct. Off by default so preview surfaces never give away the
// answer key; only the manager-facing review/grade modals opt in.
// `controlOnly` renders just the answer control (no label/help) — used by the
// form editor, which renders its own inline-editable question + helper above
// this grayed, non-interactive preview of the control.
function ReadOnlyField({
  field,
  value,
  answered = false,
  showScoring = false,
  controlOnly = false,
}) {
  // Display-only info screen — headline, message, optional image, no input.
  if (field.type === "info_screen") {
    return (
      <div className="!mt-8 first:!mt-0">
        <FormInfoScreen
          label={field.label}
          help={field.help}
          imageUrl={field.image_url}
        />
      </div>
    );
  }

  // Display-only section heading — no input, no value, no required marker.
  // An optional description (help) renders under it (the statement text in
  // slide mode).
  if (field.type === "heading") {
    return (
      <div className="!mt-10 first:!mt-0">
        <h3 className="text-base font-semibold text-slate-900 pb-2.5 border-b border-slate-200">
          {field.label || "Section"}
        </h3>
        {field.help && (
          <p className="mt-2 text-sm text-slate-600 whitespace-pre-wrap rw-form-help">
            {field.help}
          </p>
        )}
        <FormFieldImage url={field.image_url} size="inline" className="mt-3" />
      </div>
    );
  }

  const boxBase = "w-full px-3 py-2 text-sm border rounded-lg";
  const emptyBox = `${boxBase} border-gray-200 bg-gray-50 text-slate-400`;
  const filledBox = `${boxBase} border-gray-300 bg-white text-slate-900 whitespace-pre-wrap`;

  const { min, max } = scaleBounds(field);
  const scaleButtons = [];
  for (let n = min; n <= max; n++) scaleButtons.push(n);

  const hasValue = value !== undefined && value !== null && value !== "";

  return (
    <div>
      {!controlOnly && (
        <>
          <label className="block text-sm font-medium text-slate-800 mb-1.5">
            {field.label || "Untitled field"}
            {field.required && <span className="text-red-500"> *</span>}
          </label>
          {field.help && field.type !== "checkbox" && (
            <p className="text-xs text-slate-500 mb-1.5 whitespace-pre-wrap rw-form-help">
              {field.help}
            </p>
          )}
          {/* controlOnly is the editor card, which renders its own (editable)
              copy of the image just below — don't draw it twice. */}
          <FormFieldImage url={field.image_url} size="inline" className="mb-2" />
        </>
      )}

      {(field.type === "short_text" || field.type === "long_text") &&
        (answered ? (
          <div
            className={`${filledBox}${
              field.type === "long_text" ? " min-h-[3rem]" : ""
            }`}
          >
            {hasValue ? String(value) : "—"}
          </div>
        ) : (
          <div
            className={`${emptyBox}${field.type === "long_text" ? " h-16" : ""}`}
          >
            {field.type === "long_text" ? "Long text answer" : "Short text answer"}
          </div>
        ))}

      {field.type === "checkbox" && (
        <label
          className={`flex items-center gap-2 text-sm ${
            answered ? "text-slate-700" : "text-slate-500"
          }`}
        >
          <input
            type="checkbox"
            disabled
            checked={answered ? !!value : false}
            className="accent-violet-600 w-4 h-4 shrink-0"
          />
          <span className="whitespace-pre-wrap">{field.help || "Check to confirm"}</span>
        </label>
      )}

      {field.type === "yes_no" && (
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Yes", v: true },
            { label: "No", v: false },
          ].map((opt) => {
            const selected = answered && value === opt.v;
            return (
              <span
                key={opt.label}
                className={`px-6 py-2 rounded-lg text-sm border ${
                  selected
                    ? "bg-violet-700 border-violet-700 text-white"
                    : "border-gray-200 bg-gray-50 text-slate-400"
                }`}
              >
                {opt.label}
              </span>
            );
          })}
        </div>
      )}

      {field.type === "multi_select" && (
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((opt, i) => {
            const selected =
              answered && Array.isArray(value) && value.includes(opt.label);
            return (
              <span
                key={i}
                className={`px-4 py-2 rounded-lg text-sm border ${
                  selected
                    ? "bg-violet-700 border-violet-700 text-white"
                    : "border-gray-200 bg-gray-50 text-slate-400"
                }`}
              >
                {selected ? "✓ " : ""}
                {opt.label}
              </span>
            );
          })}
        </div>
      )}

      {field.type === "single_select" && (
        <div className="flex flex-wrap gap-2">
          {(field.options || []).map((opt, i) => {
            const selected = answered && value === opt.label;
            return (
              <span
                key={i}
                className={`px-4 py-2 rounded-lg text-sm border ${
                  selected
                    ? "bg-violet-700 border-violet-700 text-white"
                    : "border-gray-200 bg-gray-50 text-slate-400"
                }`}
              >
                {opt.label}
                {field.scored && showScoring && (
                  <span
                    className={`ml-1 text-xs ${
                      selected ? "text-violet-100" : "text-slate-400"
                    }`}
                  >
                    ({opt.points} pt{opt.points === 1 ? "" : "s"})
                  </span>
                )}
              </span>
            );
          })}
        </div>
      )}

      {isScaleLikeType(field.type) && (
        <div>
          <div className="flex flex-wrap gap-2">
            {scaleButtons.map((n) => {
              const selected = answered && hasValue && Number(value) === n;
              return (
                <span
                  key={n}
                  className={`h-10 flex-1 basis-10 min-w-[2.5rem] rounded-lg text-sm border flex items-center justify-center ${
                    selected
                      ? "bg-violet-700 border-violet-700 text-white"
                      : "border-gray-200 bg-gray-50 text-slate-400"
                  }`}
                >
                  {n}
                </span>
              );
            })}
          </div>
          {(field.min_label || field.max_label) && (
            <div className="mt-1.5 flex justify-between text-xs text-slate-400">
              <span>{field.min_label || ""}</span>
              <span>{field.max_label || ""}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReadOnlyField;
